import React from 'react';
import { Dialog } from '@progress/kendo-react-dialogs';
import { Input, InputChangeEvent } from '@progress/kendo-react-inputs';
import { Button } from '@progress/kendo-react-buttons';
import { useGlobalizationContext, useTranslation } from '@cs-cms/state-management';
import { PRIVATE_VIEW_NAME_MIN, PRIVATE_VIEW_NAME_MAX } from '@cs-cms/types';

const PRIVATE_VIEW_TITLE_KEY = 'custom.settings.views.dialog.createTitle';
const PRIVATE_VIEW_DESCRIPTION_KEY = 'custom.settings.views.dialog.createDescription';

interface CreateViewDialogProps {
  open: boolean;
  onClose: () => void;
  onCreateView: (viewName: string) => void;
  titleKey?: string;
  descriptionKey?: string;
}

export const CreateViewDialog: React.FC<CreateViewDialogProps> = ({
  open,
  onClose,
  onCreateView,
  titleKey = PRIVATE_VIEW_TITLE_KEY,
  descriptionKey = PRIVATE_VIEW_DESCRIPTION_KEY
}) => {
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const [newViewName, setNewViewName] = React.useState<string>('');
  const [viewNameError, setViewNameError] = React.useState<string>('');

  const handleInputChange = (e: InputChangeEvent) => {
    const value = e.value;
    setNewViewName(value);
    if (value.length < PRIVATE_VIEW_NAME_MIN) {
      setViewNameError(translate('custom.settings.views.nameLength', { count: PRIVATE_VIEW_NAME_MIN }));
    } else if (value.length > PRIVATE_VIEW_NAME_MAX) {
      setViewNameError(translate('custom.settings.views.nameLengthMax', { count: PRIVATE_VIEW_NAME_MAX }));
    } else {
      setViewNameError('');
    }
  };

  const resetForm = () => {
    setNewViewName('');
  };

  const handleCreateView = () => {
    if (newViewName.length >= 2) {
      onCreateView(newViewName);
      resetForm();
      onClose();
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCreateView();
    }
  };

  return (
    <>
      {open && (
        <Dialog
          title={translate(titleKey)}
          onClose={handleClose}
          width={450}
          className="k-dialog-centered viewNameDialog"
        >
          <div className="k-form k-form-horizontal" onKeyDown={handleKeyPress}>
            <p>{translate(descriptionKey)}</p>
            <div>
              <label className="k-label">{translate('custom.settings.views.dialog.viewNameLabel')}</label>
              <Input
                value={newViewName}
                onChange={handleInputChange}
                placeholder={translate('custom.settings.views.dialog.viewNamePlaceholder')}
              />
              {viewNameError && <div className="k-form-error k-text-error">{viewNameError}</div>}
            </div>

            <div className="k-actions k-actions-end k-actions-horizontal k-mt-4">
              <Button fillMode="flat" onClick={handleClose}>
                {translate('custom.common.cancel')}
              </Button>
              <Button
                themeColor="primary"
                onClick={handleCreateView}
                disabled={newViewName.length < PRIVATE_VIEW_NAME_MIN || newViewName.length > PRIVATE_VIEW_NAME_MAX}
              >
                {translate('custom.common.create')}
              </Button>
            </div>
          </div>
        </Dialog>
      )}
    </>
  );
};
